import { useHandler } from "@redotech/react-util/hook";
import { TrackOrderUiState } from "@redotech/redo-model/conversation";
import { TrackOrderUiMessageType } from "@redotech/redo-model/conversation/message-parsers/track-order-message-parser";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { Flex } from "../flex";
import {
  RedoLoginComponentProps,
  RedoLoginContainer,
  RedoLoginProps,
  RESEND_CODE_DELAY,
} from "../login/redo-login-container";
import { OtpInput } from "../otp-input";
import { Text } from "../text";
import { parseAuth } from "../utils/storefront-is-logged-in";
import * as css from "./chat-widget-components/order-lookup-tool-message.module.css";
import { OrderTrackingInfoMessage } from "./chat-widget-components/order-tracking-info-message";
import { GetMostRecentOrderIdFn, HandleTrackOrderFn } from "./common";

export const LookupTrackOrder = memo(function LookupTrackOrder({
  message,
  storeUrl,
  handleTrackOrder,
  getMostRecentOrderId,
  lookupOrderByEmailLoginProps,
  messageSide = "left",
}: {
  message: TrackOrderUiMessageType;
  storeUrl: string | undefined;
  handleTrackOrder: HandleTrackOrderFnProp | undefined;
  getMostRecentOrderId: GetMostRecentOrderIdFn | undefined;
  lookupOrderByEmailLoginProps: LookupOrderByEmailLoginProps | undefined;
  messageSide?: "left" | "right";
}) {
  if (!isDef(message.structuredContent)) {
    return null;
  }

  switch (message.structuredContent.state) {
    case TrackOrderUiState.FOUND_ORDER:
      return (
        <OrderTrackingInfoMessage
          messageSide={messageSide}
          storeUrl={storeUrl}
          trackableFulfillmentsToDisplay={
            message.structuredContent.trackableFulfillmentsToDisplay
          }
        />
      );
    case TrackOrderUiState.ORDER_LOOKUP:
      return (
        <Flex
          className={classNames(css.orderLookupBubbleContainer, {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [css.messageSideLeft]: messageSide === "left",
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            [css.messageSideRight]: messageSide === "right",
          })}
          dir="column"
          gap="md"
          px="lg"
          py="md"
        >
          <OrderLookup
            getMostRecentOrderId={getMostRecentOrderId}
            handleTrackOrder={handleTrackOrder}
            lookupOrderByEmailLoginProps={lookupOrderByEmailLoginProps}
          />
        </Flex>
      );
    default:
      assertNever(message?.structuredContent);
  }
});

enum OrderLookupState {
  EMAIL_AND_ORDER_ID_INPUT = "EMAIL_AND_ORDER_ID_INPUT",
  EMAIL_INPUT = "EMAIL_INPUT",
}

const OrderLookup = memo(function OrderLookup({
  handleTrackOrder,
  lookupOrderByEmailLoginProps,
  getMostRecentOrderId,
}: {
  handleTrackOrder: HandleTrackOrderFnProp | undefined;
  lookupOrderByEmailLoginProps: LookupOrderByEmailLoginProps | undefined;
  getMostRecentOrderId: GetMostRecentOrderIdFn | undefined;
}) {
  const [state, setState] = useState(OrderLookupState.EMAIL_AND_ORDER_ID_INPUT);

  switch (state) {
    case OrderLookupState.EMAIL_AND_ORDER_ID_INPUT:
      return (
        <LookupByEmailAndOrderId
          handleTrackOrder={handleTrackOrder}
          readonly={!isDef(lookupOrderByEmailLoginProps)}
          setOrderLookupState={setState}
        />
      );
    case OrderLookupState.EMAIL_INPUT:
      return (
        <LookupByEmail
          getMostRecentOrderId={getMostRecentOrderId}
          handleTrackOrder={handleTrackOrder}
          lookupOrderByEmailLoginProps={lookupOrderByEmailLoginProps}
          setOrderLookupState={setState}
        />
      );
    default:
      assertNever(state);
  }
});

const LookupByEmailAndOrderId = memo(function LookupByEmailAndOrderId({
  handleTrackOrder,
  setOrderLookupState,
  readonly,
}: {
  handleTrackOrder: HandleTrackOrderFnProp | undefined;
  setOrderLookupState: (state: OrderLookupState) => void;
  readonly: boolean;
}) {
  const [orderEmail, setOrderEmail] = useState("");
  const [orderId, setOrderId] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [lookupPending, setLookupPending] = useState(false);

  const handleForgotOrderNumber = useHandler(() => {
    setOrderLookupState(OrderLookupState.EMAIL_INPUT);
  });

  const onTrackOrder = useCallback(async () => {
    setError(undefined);
    if (isDef(handleTrackOrder)) {
      try {
        setLookupPending(true);
        await handleTrackOrder(orderEmail, orderId);
        setLookupPending(false);
      } catch (error) {
        if (error instanceof Error) {
          setLookupPending(false);
          setError(error.message);
        } else {
          setError("An unknown error occurred.");
        }
      }
    }
  }, [handleTrackOrder, orderEmail, orderId]);

  return (
    <>
      <RedoTextInput
        label="Email"
        setValue={setOrderEmail}
        state={readonly ? "disabled" : undefined}
        value={orderEmail}
      />
      <RedoTextInput
        label="Order number"
        setValue={setOrderId}
        state={readonly ? "disabled" : undefined}
        value={orderId}
      />
      {error && (
        <Text fontSize="xs" textColor="error">
          {error}
        </Text>
      )}
      <Flex alignSelf="flex-start" gap="sm">
        <RedoButton
          disabled={readonly || lookupPending}
          hierarchy="link"
          onClick={handleForgotOrderNumber}
          size="xs"
          text="Forgot order number?"
        />
        <RedoButton
          disabled={readonly || !orderEmail || !orderId}
          hierarchy="primary"
          onClick={onTrackOrder}
          pending={lookupPending}
          size="xs"
          text="Track order"
        />
      </Flex>
    </>
  );
});

enum LookupByEmailState {
  EMAIL_INPUT = "EMAIL_INPUT",
  EMAIL_CODE_INPUT = "EMAIL_CODE_INPUT",
}

export interface LookupOrderByEmailLoginProps {
  verifyOneTimePasscodeFn: RedoLoginProps["otpParams"]["verifyOneTimePasscode"];
  submitOneTimePasscodeEmailFn: RedoLoginProps["otpParams"]["submitOneTimePasscodeEmail"];
  setAuth: RedoLoginProps["setAuth"];
  teamId: RedoLoginProps["teamId"];
}

interface LookupByEmailLoginComponentProps {
  handleLookupWithOrderNumber: () => void;
}

enum LookupByEmailErrorEnum {
  TRACK_ORDER_ERROR = "TRACK_ORDER_ERROR",
  NO_ORDERS_FOUND = "NO_ORDERS_FOUND",
  INTERNAL_ERROR = "INTERNAL_ERROR",
}

type LookupByEmailError =
  | { type: LookupByEmailErrorEnum.TRACK_ORDER_ERROR; error: string }
  | { type: LookupByEmailErrorEnum.NO_ORDERS_FOUND }
  | { type: LookupByEmailErrorEnum.INTERNAL_ERROR; errorCode: string };

const LookupByEmail = memo(function LookupByEmail({
  lookupOrderByEmailLoginProps,
  setOrderLookupState,
  handleTrackOrder,
  getMostRecentOrderId,
}: {
  lookupOrderByEmailLoginProps: LookupOrderByEmailLoginProps | undefined;
  setOrderLookupState: (state: OrderLookupState) => void;
  handleTrackOrder: HandleTrackOrderFnProp | undefined;
  getMostRecentOrderId: GetMostRecentOrderIdFn | undefined;
}) {
  const [error, setError] = useState<LookupByEmailError | undefined>(undefined);

  const loginProps: RedoLoginProps = useMemo(
    () => ({
      onLogin: async (authToken: string) => {
        const decodedCustomerWidgetAuthToken = parseAuth(authToken);
        if (typeof decodedCustomerWidgetAuthToken.customerEmail !== "string") {
          console.error("Invalid auth token");
          setError({
            type: LookupByEmailErrorEnum.INTERNAL_ERROR,
            errorCode: "1001",
          });
          return;
        }
        const email = decodedCustomerWidgetAuthToken.customerEmail;
        if (isDef(handleTrackOrder) && isDef(getMostRecentOrderId)) {
          const orderId = await getMostRecentOrderId({ email, authToken });
          if (!orderId) {
            setError({ type: LookupByEmailErrorEnum.NO_ORDERS_FOUND });
            return;
          }
          try {
            await handleTrackOrder(email, orderId);
          } catch (error) {
            if (error instanceof Error) {
              setError({
                type: LookupByEmailErrorEnum.TRACK_ORDER_ERROR,
                error: error.message || "Failed to track order",
              });
            } else {
              setError({
                type: LookupByEmailErrorEnum.TRACK_ORDER_ERROR,
                error: "An unknown error occurred.",
              });
            }
          }
        } else {
          setError({
            type: LookupByEmailErrorEnum.INTERNAL_ERROR,
            errorCode: "1002",
          });
        }
      },
      otpParams: {
        submitOneTimePasscodeEmail:
          lookupOrderByEmailLoginProps?.submitOneTimePasscodeEmailFn,
        verifyOneTimePasscode:
          lookupOrderByEmailLoginProps?.verifyOneTimePasscodeFn,
      },
      setAuth: lookupOrderByEmailLoginProps?.setAuth,
      teamId: lookupOrderByEmailLoginProps?.teamId,
      showMarketingCheckbox: false,
      subscribeToMarketing: () => {},
    }),
    [lookupOrderByEmailLoginProps, handleTrackOrder, getMostRecentOrderId],
  );

  const handleLookupWithOrderNumber = useHandler(() => {
    setOrderLookupState(OrderLookupState.EMAIL_AND_ORDER_ID_INPUT);
  });

  switch (error?.type) {
    case LookupByEmailErrorEnum.TRACK_ORDER_ERROR:
      return (
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" textColor="error">
            {error.error}
          </Text>
        </Flex>
      );
    case LookupByEmailErrorEnum.NO_ORDERS_FOUND:
      return (
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" textColor="error">
            No orders found for this email.
          </Text>
          <RedoButton
            hierarchy="primary"
            onClick={() => setError(undefined)}
            size="xs"
            text="Try a different email"
          />
        </Flex>
      );
    case LookupByEmailErrorEnum.INTERNAL_ERROR:
      return (
        <Flex dir="column" gap="sm">
          <Text fontSize="xs" textColor="error">
            Internal error. Something went wrong. Error code: {error.errorCode}.
          </Text>
          <RedoButton
            hierarchy="primary"
            onClick={() => setError(undefined)}
            size="xs"
            text="Try again"
          />
        </Flex>
      );
    case undefined:
      return (
        <RedoLoginContainer
          LoginComponent={LookupByEmailLoginComponent}
          loginComponentProps={{ handleLookupWithOrderNumber }}
          loginProps={loginProps}
        />
      );
    default:
      assertNever(error);
  }
});

const LookupByEmailLoginComponent = memo(function LookupByEmailLoginComponent({
  emailEntered,
  fourDigitCodeId,
  textParams,
  fourDigitCodeInput,
  fourDigitCodeSubmit,
  doFourDigitCodeSubmit,
  secondsSinceLastSent,
  emailSubmit,
  handleEmailSubmit,
  handleChangeEmail,
  showIncorrectCodeError,
  emailInput,
  loginComponentProps,
}: RedoLoginComponentProps<LookupByEmailLoginComponentProps>) {
  const state = useMemo(() => {
    if (!!emailEntered && fourDigitCodeId) {
      return LookupByEmailState.EMAIL_CODE_INPUT;
    }
    return LookupByEmailState.EMAIL_INPUT;
  }, [emailEntered, fourDigitCodeId]);

  switch (state) {
    case LookupByEmailState.EMAIL_INPUT:
      return (
        <>
          <RedoTextInput
            label="Lookup order by email"
            placeholder="Enter your email"
            setValue={emailInput.setValue}
            value={emailInput.value}
          />
          <Flex alignSelf="flex-start" gap="sm">
            <RedoButton
              hierarchy="link"
              onClick={loginComponentProps.handleLookupWithOrderNumber}
              size="xs"
              text="Use order number"
            />
            <RedoButton
              disabled={
                emailInput.errors.length > 0 ||
                !emailInput.value ||
                emailSubmit.pending
              }
              hierarchy="primary"
              onClick={handleEmailSubmit}
              pending={emailSubmit.pending}
              size="xs"
              text="Send one-time passcode"
            />
          </Flex>
        </>
      );
    case LookupByEmailState.EMAIL_CODE_INPUT: {
      const sendCodeText = `Send another code${
        !emailSubmit.pending &&
        isDef(secondsSinceLastSent) &&
        secondsSinceLastSent <= RESEND_CODE_DELAY
          ? ` (${RESEND_CODE_DELAY - secondsSinceLastSent + 1})`
          : ""
      }`;
      return (
        <>
          <Text
            fontSize={textParams?.fontSize}
            fontWeight={textParams?.fontWeight}
          >
            We sent a 4-digit code to <strong>{emailEntered}</strong>. Please
            enter the code below.
          </Text>
          <OtpInput
            numeric
            onChange={(value) => {
              fourDigitCodeInput.setValue(value);
              if (value.join("").length === 4) {
                doFourDigitCodeSubmit();
              }
            }}
            values={fourDigitCodeInput.value}
          />
          <Flex alignSelf="flex-start" gap="sm">
            <RedoButton
              disabled={fourDigitCodeSubmit.pending}
              hierarchy="link"
              onClick={handleChangeEmail}
              size="xs"
              text="Change email"
            />
            <RedoButton
              disabled={
                emailSubmit.pending ||
                (isDef(secondsSinceLastSent) &&
                  secondsSinceLastSent <= RESEND_CODE_DELAY)
              }
              hierarchy="secondary"
              onClick={handleEmailSubmit}
              pending={fourDigitCodeSubmit.pending}
              size="xs"
              text={sendCodeText}
            />
          </Flex>
          {showIncorrectCodeError && (
            <Text
              fontSize={textParams?.fontSize}
              fontWeight={textParams?.fontWeight}
              textColor="error"
            >
              The code you entered is incorrect. Please check your email for
              another code and try again.
            </Text>
          )}
        </>
      );
    }
    default:
      assertNever(state);
  }
});

type HandleTrackOrderFnProp = (
  email: string,
  orderId: string,
) => ReturnType<HandleTrackOrderFn>;
